'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { ProcessStageView } from '../lib/process-stage-constants';

interface ProcessStageModalProps {
  stage: ProcessStageView;
  onClose: () => void;
}

export default function ProcessStageModal({ stage, onClose }: ProcessStageModalProps) {
  const t = useTranslations('ourProces.section');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    const previousOverflow = document.body.style.overflow;

    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  if (!mounted) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-[#111111]/72 p-4 backdrop-blur-md sm:p-5"
      style={{ animation: 'process-fade-in 0.2s ease' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="process-stage-modal-title"
    >
      <div
        className="relative flex max-h-[min(92svh,90vh)] w-full max-w-[600px] flex-col overflow-hidden rounded-[10px] border border-black/[0.08] border-t-4 border-t-[#EC1C24] bg-[#F0EDE8] shadow-[0_40px_100px_rgba(0,0,0,0.22),0_8px_32px_rgba(0,0,0,0.12)]"
        style={{ animation: 'process-slide-up 0.3s ease' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 bg-[#111111] px-5 py-5 sm:px-8 sm:py-6">
          <div className="min-w-0 flex-1">
            <p className="mb-1.5 text-[8px] font-bold uppercase tracking-[0.25em] text-[#EC1C24] sm:text-[9px]">
              {stage.subtitle}
            </p>
            <h2
              id="process-stage-modal-title"
              className="text-lg font-semibold tracking-tight text-white sm:text-xl"
            >
              {stage.title}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white/80 transition-colors hover:bg-white/20 hover:text-white"
            aria-label={t('close')}
          >
            <X className="h-4 w-4" strokeWidth={2.25} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-8 sm:py-8">
          {stage.detailDesc ? (
            <p className="mb-6 text-[13.5px] font-light leading-[1.85] text-black/50 sm:mb-7 sm:text-[14.5px] sm:leading-[1.9]">
              {stage.detailDesc}
            </p>
          ) : null}

          {stage.stats.length > 0 ? (
            <div className="grid grid-cols-3 gap-px overflow-hidden rounded-lg bg-black/[0.08] shadow-[0_2px_10px_rgba(0,0,0,0.06)]">
              {stage.stats.map((stat) => (
                <div
                  key={`${stat.label}-${stat.value}`}
                  className="bg-white px-2 py-4 text-center sm:px-3.5 sm:py-5"
                >
                  <div className="mb-1 text-[17px] font-semibold tracking-tight text-[#EC1C24] sm:mb-1.5 sm:text-[22px]">
                    {stat.value}
                  </div>
                  <div className="text-[7px] uppercase leading-tight tracking-[0.12em] text-black/35 sm:text-[8px] sm:tracking-[0.2em]">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
